"use client"

import { parseEditorJS, extractHeaders } from "@/lib/editorjs-parser"
import TableOfContents from "@/components/TableOfContents"
import { usePosts } from "@letterhead/core/react"
import { useEffect, useState } from "react"
import { Post } from "@letterhead/core"
import { useParams } from "next/navigation"
import Link from "next/link"
import './blog.scss'
import { ArrowLeft } from 'lucide-react';
import { useLocale, useTranslations } from "next-intl"

function BlogPostContent() {
  const t = useTranslations('blogPage')
  const params = useParams()
  const blogId = params.blogId as string
  const posts = usePosts()
  const [post, setPost] = useState<Post | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const locale = useLocale()

  useEffect(() => {
    async function fetchPost() {
      try {
        // Try to fetch by slug first, if that fails try by ID
        const fetchedPost = await posts.getBySlug(blogId).catch(() => posts.get(blogId))
        
        let targetPost = { ...fetchedPost };

        if (locale !== 'en' && targetPost) {
           try {
               let originalContent = targetPost.content;
               let originalTitle = targetPost.title || '';
               
               // EditorJS content is often stored as JSON. For safety in automatic translation, 
               // we translate specific block data (paragraphs, headers, lists)
               let isEditorJS = false;
               let textsToTranslate: string[] = [originalTitle];
               let blockTextRefs: { index: number, path: (string | number)[], text: string }[] = [];
               let contentObj: any = null;
               
               try {
                  contentObj = typeof originalContent === 'string' ? JSON.parse(originalContent) : originalContent;
                  if (contentObj && contentObj.blocks && Array.isArray(contentObj.blocks)) {
                      isEditorJS = true;
                      
                      contentObj.blocks.forEach((block: any, i: number) => {
                          if (block.type === 'paragraph' || block.type === 'header' || block.type === 'quote') {
                              if (block.data?.text) {
                                  blockTextRefs.push({ index: i, path: ['data', 'text'], text: block.data.text });
                              }
                          } else if (block.type === 'list' && Array.isArray(block.data?.items)) {
                              block.data.items.forEach((item: any, j: number) => {
                                  if (typeof item === 'string') {
                                      blockTextRefs.push({ index: i, path: ['data', 'items', j], text: item });
                                  } else if (item && item.content) {
                                      blockTextRefs.push({ index: i, path: ['data', 'items', j, 'content'], text: item.content });
                                  }
                              });
                          }
                      });

                      textsToTranslate.push(...blockTextRefs.map(b => b.text));
                  }
               } catch(e) {
                   isEditorJS = false;
               }

               if (!isEditorJS && typeof originalContent === 'string') {
                   textsToTranslate.push(originalContent);
               }

               if (textsToTranslate.length > 0) {
                   const res = await fetch('/api/translate', {
                       method: 'POST',
                       headers: { 'Content-Type': 'application/json' },
                       body: JSON.stringify({ texts: textsToTranslate, targetLang: locale })
                   });
                   
                   if (res.ok) {
                       const data = await res.json();
                       if (data.translated && Array.isArray(data.translated)) {
                           targetPost.title = data.translated[0] || targetPost.title;
                           
                           if (isEditorJS && contentObj) {
                               for (let i = 0; i < blockTextRefs.length; i++) {
                                   const translatedText = data.translated[i + 1];
                                   if (translatedText) {
                                       const { index, path } = blockTextRefs[i];
                                       let target = contentObj.blocks[index];
                                       for (let p = 0; p < path.length - 1; p++) {
                                           target = target[path[p]];
                                       }
                                       target[path[path.length - 1]] = translatedText;
                                   }
                               }
                               targetPost.content = JSON.stringify(contentObj);
                           } else if (!isEditorJS && data.translated[1]) {
                               targetPost.content = data.translated[1];
                           }
                       }
                   }
               }
           } catch (translationErr) {
               console.error("Translation feature suppressed:", translationErr);
           }
        }

        setPost(targetPost)
      } catch (err: any) {
        setError(err.message || "Failed to load post")
      } finally {
        setIsLoading(false)
      }
    }
    fetchPost()
  }, [blogId, posts, locale])

  if (isLoading) {
    return (
      <div className="container mx-auto py-20">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/4" />
            <div className="space-y-3 mt-8">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="container mx-auto py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">{t("postNotFound")}</h1>
          <p className="text-gray-600 mb-8">
            {error || t("postNotFoundDesc")}
          </p>
          <Link
            href="/blog"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← {t("backToBlog")}
          </Link>
        </div>
      </div>
    )
  }

  // Parse the content (could be stored as string or object)
  let parsedContent
  let headers: any[] = []

  try {
    const contentData = typeof post.content === "string"
      ? JSON.parse(post.content)
      : post.parsed || post.content

    if (contentData) {
      headers = extractHeaders(contentData)
      parsedContent = parseEditorJS(contentData)
    }
  } catch (err) {
    parsedContent = post.content
  }

  return (
    <div className="container mx-auto py-20">

      {/* Content with TOC */}
      <div className={`blog-layout ${headers.length === 0 ? 'no-sidebar' : ''}`}>
        {headers.length > 0 && (
          <aside className="toc-sidebar">
            <TableOfContents headers={headers} />
          </aside>
        )}
        <div>
          {/* Header */}
          <div className="max-w-4xl mx-auto mb-12">
            <Link
              href="/blog"
              className="inline-flex text-[16px] items-center text-gray-400 hover:text-gray-500 mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("backToBlog")}
            </Link>

            <div className="space-y-4">
              <h1 className="tex-3xl md:text-[2.5rem]">{post.title}</h1>

              <div className="flex items-center gap-4 mt-6">
                {post.author?.image && (
                  <div className="relative h-12 w-12 overflow-hidden rounded-full bg-gray-100">
                    <img
                      src={post.author.image}
                      alt={post.author.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  {post.author?.name && (
                    <span className="text-base font-semibold text-gray-900">
                      {post.author.name}
                    </span>
                  )}

                  <div className="flex items-center text-sm text-gray-400">
                    <span className="uppercase font-medium tracking-wider">
                      {new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>

                    {(() => {
                      const displayTags = post.metadata?.seo_tags || post.tags || [];
                      return displayTags.length > 0 ? (
                        <>
                          <span className="mx-2">•</span>
                          <div className="flex flex-wrap gap-2">
                            {displayTags.map((tag, idx) => (
                              <Link
                                key={idx}
                                href={`/blog/tags/${tag}`}
                                className="text-[#08C4DE] hover:underline uppercase font-medium tracking-wider"
                              >
                                {tag}
                              </Link>
                            ))}
                          </div>
                        </>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <article className="blog_post">
            {parsedContent ? (
              <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
            ) : (
              <div className="prose prose-lg max-w-none">
                <p>Content not available</p>
              </div>
            )}
          </article>

          {/* Tags */}

        </div>
      </div>
    </div>
  )
}

export default function BlogPostClient() {
  return <BlogPostContent />
}
